import { inject, Injectable } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { BehaviorSubject, Subject } from "rxjs";
import { IBreadcrumb, IPageAction } from "../models/common";

@Injectable({
  providedIn: "root",
})
export class BreadcrumbService {
  private readonly breadcrumbSubscription = new Subject<any>();
  private readonly eventsSubscription = new Subject<any>();
  /** Fires when a breadcrumb dropdown node's value changes. Payload: { key, value } */
  private readonly dropdownEventsSubscription = new Subject<{ key: string; value: string }>();
  private readonly pageActionsSubject = new BehaviorSubject<IPageAction[]>([]);
  private readonly pageActionEventSubject = new Subject<string>();
  list: any = [];
  modal: any;
  private readonly modalService = inject(NgbModal);

  constructor() {}

  addNode(data: IBreadcrumb, modal?: any) {
    if (modal) {
      this.modal = modal;
    }
    let isExist = this.list.find((item: IBreadcrumb) => item.key === data.key);
    if (!isExist) {
      this.list.push(data);
      this.update();
    }
  }

  updateNode(data: IBreadcrumb) {
    let index = this.list.findIndex(
      (item: IBreadcrumb) => data.key === item.key
    );
    if (index >= 0) {
      this.list[index] = data;
      this.update();
    }
  }

  updateNodeByLevel(data: IBreadcrumb) {
    let index = this.list.findIndex(
      (item: IBreadcrumb) => data.level === item.level
    );
    if (index >= 0) {
      this.list[index] = data;
      this.list = this.list.slice(0, index + 1);
      this.update();
    }
  }

  deleteNode(data: IBreadcrumb) {
    let index = this.list.findIndex(
      (item: IBreadcrumb) => data.key === item.key
    );
    if (index >= 0) {
      this.list.splice(index, 1);
      this.update();
    }
  }

  private update() {
    this.breadcrumbSubscription.next(this.list);
  }

  getBreadcrumb() {
    return this.breadcrumbSubscription.asObservable();
  }

  clear() {
    this.list = [];
    this.breadcrumbSubscription.next(null);
  }

  fireClickAction(data: IBreadcrumb) {
    if (!data.clickable) {
      return;
    }
    const index = this.list.findIndex(
      (item: IBreadcrumb) => item.key === data.key
    );
    if (!index) {
      this.list = [data];
    } else if(this.list.length > 1) {
      // Keep nodes up to AND including the clicked node (trim everything after it)
      this.list = this.list.slice(0, index + 1);
    }
    this.eventsSubscription.next(data);
    this.update();
  }

  onNodeClick() {
    return this.eventsSubscription.asObservable();
  }

  /** Called when the user picks a different item from a dropdown breadcrumb node. */
  fireDropdownChange(key: string, value: string): void {
    const node = this.list.find((item: IBreadcrumb) => item.key === key);
    if (node) {
      node.selectedDropdownValue = value;
      const selected = node.dropdownItems?.find(
        (i: { label: string; value: string }) => i.value === value
      );
      node.name = selected ? selected.label : value;
      this.update();
    }
    this.dropdownEventsSubscription.next({ key, value });
  }

  /** Observable that emits whenever a dropdown breadcrumb node value changes. */
  onDropdownChange() {
    return this.dropdownEventsSubscription.asObservable();
  }

  setPageActions(actions: IPageAction[]): void {
    this.pageActionsSubject.next(actions);
  }

  getPageActions$() {
    return this.pageActionsSubject.asObservable();
  }

  clearPageActions(): void {
    this.pageActionsSubject.next([]);
  }

  firePageAction(key: string): void {
    this.pageActionEventSubject.next(key);
  }

  onPageAction() {
    return this.pageActionEventSubject.asObservable();
  }

  unSubscribe() {
    this.breadcrumbSubscription.unsubscribe();
    this.eventsSubscription.unsubscribe();
  }
}
